import React, { useState } from "react";

const highlightGraphQL = (code) => {
  const keywords = ['query', 'mutation', 'subscription', 'fragment', 'on', 'type', 'interface', 'enum', 'union', 'scalar', 'input', 'extend', 'implements'];

  const lines = code.split('\n');

  return lines.map((line, lineIndex) => {
    const tokens = [];
    let currentPos = 0;
    let remainingLine = line;

    // Process the line token by token
    while (remainingLine.length > 0) {
      let matched = false;

      // Check for comment
      const commentMatch = remainingLine.match(/^(#.*)/);
      if (commentMatch) {
        tokens.push(<span key={`${lineIndex}-${currentPos}`} className="comment">{commentMatch[1]}</span>);
        break;
      }

      // Check for string
      const stringMatch = remainingLine.match(/^("(?:[^"\\]|\\.)*")/);
      if (stringMatch) {
        tokens.push(<span key={`${lineIndex}-${currentPos}`} className="string">{stringMatch[1]}</span>);
        remainingLine = remainingLine.slice(stringMatch[1].length);
        currentPos += stringMatch[1].length;
        matched = true;
        continue;
      }

      // Check for variable
      const varMatch = remainingLine.match(/^(\$\w+)/);
      if (varMatch) {
        tokens.push(<span key={`${lineIndex}-${currentPos}`} className="variable">{varMatch[1]}</span>);
        remainingLine = remainingLine.slice(varMatch[1].length);
        currentPos += varMatch[1].length;
        matched = true;
        continue;
      }

      // Check for keyword
      let keywordMatched = false;
      for (const keyword of keywords) {
        const keywordRegex = new RegExp(`^(${keyword})\\b`);
        const keywordMatch = remainingLine.match(keywordRegex);
        if (keywordMatch) {
          tokens.push(<span key={`${lineIndex}-${currentPos}`} className="keyword">{keywordMatch[1]}</span>);
          remainingLine = remainingLine.slice(keywordMatch[1].length);
          currentPos += keywordMatch[1].length;
          keywordMatched = true;
          matched = true;
          break;
        }
      }
      if (keywordMatched) continue;

      // Check for field name (word before { or ()
      const fieldMatch = remainingLine.match(/^(\w+)(?=\s*[{(])/);
      if (fieldMatch) {
        tokens.push(<span key={`${lineIndex}-${currentPos}`} className="function">{fieldMatch[1]}</span>);
        remainingLine = remainingLine.slice(fieldMatch[1].length);
        currentPos += fieldMatch[1].length;
        matched = true;
        continue;
      }

      // No special token, just take one character
      tokens.push(<span key={`${lineIndex}-${currentPos}`}>{remainingLine[0]}</span>);
      remainingLine = remainingLine.slice(1);
      currentPos += 1;
    }

    return <div key={lineIndex}>{tokens}</div>;
  });
};

const highlightJSON = (code) => {
  const lines = code.split('\n');

  return lines.map((line, lineIndex) => {
    const tokens = [];
    let remainingLine = line;
    let currentPos = 0;

    while (remainingLine.length > 0) {
      // Check for property name (string before colon)
      const propMatch = remainingLine.match(/^(\s*)"([^"]+)"(\s*:)/);
      if (propMatch) {
        tokens.push(<span key={`${lineIndex}-${currentPos}-ws`}>{propMatch[1]}</span>);
        tokens.push(<span key={`${lineIndex}-${currentPos}`} className="property">"{propMatch[2]}"</span>);
        tokens.push(<span key={`${lineIndex}-${currentPos}-colon`}>{propMatch[3]}</span>);
        remainingLine = remainingLine.slice(propMatch[0].length);
        currentPos += propMatch[0].length;
        continue;
      }

      // Check for string value
      const stringMatch = remainingLine.match(/^("(?:[^"\\]|\\.)*")/);
      if (stringMatch) {
        tokens.push(<span key={`${lineIndex}-${currentPos}`} className="string">{stringMatch[1]}</span>);
        remainingLine = remainingLine.slice(stringMatch[1].length);
        currentPos += stringMatch[1].length;
        continue;
      }

      // Check for number
      const numMatch = remainingLine.match(/^(-?\d+\.?\d*)/);
      if (numMatch && remainingLine[0].match(/[\d-]/)) {
        tokens.push(<span key={`${lineIndex}-${currentPos}`} className="number">{numMatch[1]}</span>);
        remainingLine = remainingLine.slice(numMatch[1].length);
        currentPos += numMatch[1].length;
        continue;
      }

      // Check for boolean/null
      const boolMatch = remainingLine.match(/^(true|false|null)\b/);
      if (boolMatch) {
        tokens.push(<span key={`${lineIndex}-${currentPos}`} className="keyword">{boolMatch[1]}</span>);
        remainingLine = remainingLine.slice(boolMatch[1].length);
        currentPos += boolMatch[1].length;
        continue;
      }

      // Regular character
      tokens.push(<span key={`${lineIndex}-${currentPos}`}>{remainingLine[0]}</span>);
      remainingLine = remainingLine.slice(1);
      currentPos += 1;
    }

    return <div key={lineIndex}>{tokens}</div>;
  });
};

const highlightBash = (code) => {
  const lines = code.split('\n');
  const commands = ['curl', 'echo', 'cat', 'grep', 'awk', 'sed', 'node', 'npm', 'yarn', 'git', 'docker', 'cd', 'ls', 'mkdir', 'rm', 'cp', 'mv'];

  return lines.map((line, lineIndex) => {
    const tokens = [];
    let remainingLine = line;
    let currentPos = 0;
    let isStartOfLine = true;

    while (remainingLine.length > 0) {
      // Check for comment
      const commentMatch = remainingLine.match(/^(#.*)/);
      if (commentMatch) {
        tokens.push(<span key={`${lineIndex}-${currentPos}`} className="comment">{commentMatch[1]}</span>);
        break;
      }

      // Check for string
      const stringMatch = remainingLine.match(/^(["'](?:[^"'\\]|\\.)*["'])/);
      if (stringMatch) {
        tokens.push(<span key={`${lineIndex}-${currentPos}`} className="string">{stringMatch[1]}</span>);
        remainingLine = remainingLine.slice(stringMatch[1].length);
        currentPos += stringMatch[1].length;
        isStartOfLine = false;
        continue;
      }

      // Check for command at start
      if (isStartOfLine || remainingLine.match(/^\s/)) {
        const wsMatch = remainingLine.match(/^(\s+)/);
        if (wsMatch) {
          tokens.push(<span key={`${lineIndex}-${currentPos}`}>{wsMatch[1]}</span>);
          remainingLine = remainingLine.slice(wsMatch[1].length);
          currentPos += wsMatch[1].length;
          continue;
        }

        for (const cmd of commands) {
          const cmdMatch = remainingLine.match(new RegExp(`^(${cmd})\\b`));
          if (cmdMatch) {
            tokens.push(<span key={`${lineIndex}-${currentPos}`} className="keyword">{cmdMatch[1]}</span>);
            remainingLine = remainingLine.slice(cmdMatch[1].length);
            currentPos += cmdMatch[1].length;
            isStartOfLine = false;
            continue;
          }
        }
      }

      // Check for flags
      const flagMatch = remainingLine.match(/^(-+[\w-]+)/);
      if (flagMatch) {
        tokens.push(<span key={`${lineIndex}-${currentPos}`} className="variable">{flagMatch[1]}</span>);
        remainingLine = remainingLine.slice(flagMatch[1].length);
        currentPos += flagMatch[1].length;
        continue;
      }

      // Regular character
      tokens.push(<span key={`${lineIndex}-${currentPos}`}>{remainingLine[0]}</span>);
      remainingLine = remainingLine.slice(1);
      currentPos += 1;
      isStartOfLine = false;
    }

    return <div key={lineIndex}>{tokens}</div>;
  });
};

export const CodeExample = ({ title, code, language = "graphql" }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const renderHighlighted = () => {
    switch (language) {
      case 'graphql':
        return highlightGraphQL(code);
      case 'json':
        return highlightJSON(code);
      case 'bash':
        return highlightBash(code);
      default:
        return code;
    }
  };

  return (
    <div className="code-example">
      {title && <div className="code-example-title">{title}</div>}
      <div className="code-example-container">
        <button
          className="code-example-copy-btn"
          onClick={handleCopy}
          aria-label="Copy code"
        >
          {copied ? "✓ Copied!" : "Copy"}
        </button>
        <pre className="code-example-pre">
          <code className={`language-${language}`}>
            {renderHighlighted()}
          </code>
        </pre>
      </div>
    </div>
  );
};


// WEBPACK FOOTER //
// ./src/components/CodeExample.jsx